import {uploadProductMediaService} from '@/services/productMedia'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useCallback, useEffect, useState} from 'react'

const initialFileErrorState = {feedbackType: 'info', helperText: ''}
const mediaSlotOrders = [1, 2, 3]
const emptyExistingMedias = []

const getFileNameFromMediaUrl = mediaUrl => {
  if (!mediaUrl) return ''

  const path = mediaUrl.split('?')[0]
  const fileName = path.split('/').pop() || ''

  try {
    return decodeURIComponent(fileName)
  } catch {
    return fileName
  }
}

const buildExistingMediaMetadata = (media, displayOrder) => ({
  displayOrder,
  mediaType: media?.mediaType || 'IMAGE',
  mediaTitle: media?.mediaTitle || 'Product',
  originalFileName: media?.originalFileName || getFileNameFromMediaUrl(media?.mediaUrl),
  referenceEntity: 'product',
})

const createMediaSlot = (displayOrder, existingMedias = []) => {
  const existing = existingMedias.find(media => Number(media.displayOrder) === displayOrder)

  return {
    slotId: `slot-${displayOrder}`,
    displayOrder,
    file: null,
    imageUri: existing?.mediaUrl || '',
    metadata: existing ? buildExistingMediaMetadata(existing, displayOrder) : null,
    uploading: false,
    error: initialFileErrorState,
  }
}

const createMediaSlots = (existingMedias = []) => mediaSlotOrders.map(order => createMediaSlot(order, existingMedias))

/**
 * Custom hook for managing multiple media uploads (up to 3 images)
 * @param {string} productId - The product ID for naming files and upload context
 * @returns {Object} Media upload state and handlers
 */
export const useMediaUpload = (productId, existingMedias = emptyExistingMedias) => {
  const dispatch = useAppDispatch()

  const [mediaSlots, setMediaSlots] = useState(() => createMediaSlots(existingMedias))
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    const serverSlots = createMediaSlots(existingMedias)

    setMediaSlots(prev =>
      serverSlots.map(serverSlot => {
        const currentSlot = prev.find(slot => slot.displayOrder === serverSlot.displayOrder)
        return currentSlot?.file ? currentSlot : {...serverSlot, error: currentSlot?.error || initialFileErrorState}
      })
    )
  }, [existingMedias])

  const updateMediaSlot = useCallback((displayOrder, updates) => {
    setMediaSlots(prev => prev.map(slot => (slot.displayOrder === displayOrder ? {...slot, ...updates} : slot)))
  }, [])

  const clearMediaSlot = useCallback(
    displayOrder => {
      updateMediaSlot(displayOrder, {
        file: null,
        imageUri: '',
        metadata: null,
      })
    },
    [updateMediaSlot]
  )

  const uploadMedias = useCallback(async () => {
    const slotsWithFiles = mediaSlots.filter(slot => slot.file && slot.metadata)
    const firstSlot = mediaSlots.find(slot => slot.displayOrder === 1)
    const hasExistingFirstSlotImage = Boolean(firstSlot?.imageUri && !firstSlot?.file)

    if (slotsWithFiles.length === 0) {
      if (hasExistingFirstSlotImage) return true

      dispatch(
        setToastNotifications({
          show: true,
          message: 'Please select at least one image to upload',
          type: 'warning',
        })
      )
      return false
    }

    try {
      setIsUploading(true)

      const formData = new FormData()

      const allMetadata = slotsWithFiles.map(slot => ({
        ...slot.metadata,
        id: productId,
      }))

      slotsWithFiles.forEach(slot => {
        formData.append(`images`, slot.file)
      })

      formData.append('metadata', JSON.stringify({mediaUploadMetadata: allMetadata}))

      await uploadProductMediaService({
        productId,
        formData,
      })

      setMediaSlots(prev => prev.map(slot => (slot.file ? {...slot, file: null} : slot)))

      return true
    } catch (error) {
      dispatch(
        setToastNotifications({
          show: true,
          message: error?.message || 'Failed to upload images',
          type: 'error',
        })
      )
      return false
    } finally {
      setIsUploading(false)
    }
  }, [mediaSlots, dispatch, productId])

  return {
    mediaSlots,
    isUploading,
    updateMediaSlot,
    clearMediaSlot,
    uploadMedias,
  }
}
